<template>
  <div style="width: 100%">
    <div style="padding-bottom: 32px;">
      <span class="titre1">{{ title }}</span>
    </div>
    <v-form ref="form" lazy-validation @submit.prevent="checkCgu" @keydown.enter.self="checkCgu">
      <v-row>
        <v-col class="col-12 login">

          <edit-identifiant ref="IDENTIFIANT"
                            v-model:value=identifiant
                            :context-id="0"
                            :theme="EditFieldTheme.OUTLINED"
                            @status=setIdentifiantError
                            @update:value="setIdentifiant">
          </edit-identifiant>

          <edit-field v-show="usePassword"
                      ref="PASSWORD"
                      :context-id="0"
                      :edit-field-info="passwordFieldInfo"
                      :required="true"
                      :value="password"
                      :password="true"
                      @error="setPasswordError"
                      @updated="setPassword">
          </edit-field>


          <edit-field v-show="useCode"
                      ref="CODE"
                      :context-id="0"
                      :edit-field-info="codeFieldInfo"
                      :required="true"
                      :value="code"
                      @error="setCodeError"
                      @updated="setCode">
          </edit-field>
          <div v-if="error">
            <p class="text-12 text-bold error-text">{{ errorTxt }}</p>
          </div>

          <div v-if="usePassword || useCode" class="text-center">
            <div class="modale-text" v-html="html"></div>
          </div>
        </v-col>

        <v-col class="col-12">
          <div class="text-center">
            <loader-button :label="loginBtnTxt"
                           type="submit"
                           :disabled="!updatedAndValid"
                           :loading="isLoading"
                           :full-width="true">
            </loader-button>
          </div>
        </v-col>

        <v-col v-show="usePassword || useCode" class="col-12">
          <div v-if="!delayReset" class="text-center">
            <a class="reset-link"
               @click="resetIdent">
              {{ resetTxt }}
            </a>
          </div>
          <div v-else class="text-center">
            <div class="text-decoration-none text-primary text-14">
              {{ resetTxt }} {{ delayLabel }}
            </div>
          </div>
        </v-col>
      </v-row>
    </v-form>
    <section-cgu v-if=cguOpen :dialog-open="cguOpen" :identifiant="identifiant" @cgu-accepted="login"></section-cgu>
    <section-alert ref="alert"></section-alert>
  </div>
</template>

<script lang="ts">
  import { isMobile } from "@/style/StyleHelpers";
  import { storeSession } from '@/generated/stores';
  import { ConnectionParamModes, LoginAnswer, LoginParam } from '@/generated/classes/Session';
  import User from '@/generated/classes/User';
  import Dialog from '@/generated/classes/Dialog';
  import { Alerts } from '@/framework/base/helpers/Alerts';
  import UserProfile, { EnumProfile } from '@/framework/base/classes/UserProfile';
  import LoaderButton from '@/framework/base/components/LoaderButton.vue';
  import {
    FwkButtons,
    FwkControls,
    FwkSections,
    AppCustoms,
    RessourceFwkButton,
    RessourceFwkControl,
    RessourceFwkSection,
    RessourceAppCustom
  } from '@/ressources/globalRessources';
  import EditField from '@/framework/base/components/EditField.vue';
  import { EditFieldTheme, EditFieldType, FieldError, FieldInfo } from '@/framework/base/components/FieldInfo';
  import EditIdentifiant from '@/framework/base/components/EditIdentifiant.vue'
  import SectionAlert from '@/framework/base/components/SectionAlert.vue';
  import SectionCgu from '@/custom/sections/SectionCgu.vue';
  import { defineComponent, type PropType } from "vue";
  import { useRoute, useRouter } from "vue-router";
  import EditCurrency from "@/framework/base/components/EditCurrency.vue";

  export default defineComponent({
    name: 'LoginComponent',
    components: {
      EditCurrency,
      EditField,
      LoaderButton,
      EditIdentifiant,
      SectionAlert,
      SectionCgu
    },
    props: {
      showDialog: {
        type: Boolean
      },
      loginErrorDialog: {
        type: Object as PropType<Dialog>
      },
      identifiantQuery: {
        type: String
      }
    },
    data() {
      const param: LoginParam = new LoginParam();

      return {
        isMobile: isMobile(),
        html: "",
        useCode: false,
        usePassword: false,
        identifiant: '',
        password: '',
        code: '',
        isLoading: false,
        rememberMe: false,
        error: false,
        delayReset: false,
        delay: 0,
        delayLabel: '',
        delayRemaining: 0,
        identifiantError: false,
        passwordError: false,
        codeError: false,
        rememberMeError: false,
        cguOpen: false,
        param,
        router: useRouter(),
        route: useRoute()
      };
    },
    computed: {
      EditFieldTheme() {
        return EditFieldTheme;
      },
      resetTxt(): string {
        if (this.usePassword) {
          return RessourceFwkSection(FwkSections.FORGOT_PASSWORD).label;
        } else if (this.useCode) {
          return RessourceFwkSection(FwkSections.CODE_NOT_RECEIVED).label;
        }
        return '';
      },
      title() {
        return RessourceFwkSection(FwkSections.LOGIN_TITRE).label;
      },
      loginBtnTxt() {
        return RessourceFwkButton(FwkButtons.LOGIN).label;
      },
      errorTxt() {
        return RessourceFwkSection(FwkSections.LOGIN_ERROR).label;
      },
      identifiantIsNumber(): boolean {
        const regEx = /^[0-9 ]+$/;
        return regEx.test(this.identifiant);
      },
      updatedAndValid(): boolean {
        if (!this.identifiant || this.identifiantError) {
          return false;
        }
        if (this.useCode && (!this.code || this.codeError)) {
          return false;
        }
        if (this.usePassword && (!this.password || this.passwordError)) {
          return false;
        }
        return true;
      },
      passwordFieldInfo(): FieldInfo {
        const ressource = RessourceFwkControl(FwkControls.PASSWORD);
        const type = EditFieldType.STRING;
        const precision = 2;
        const format = null;
        const mask = null;
        const focus = null;
        const live = null;
        return new FieldInfo(ressource, type, precision, mask, format, focus, live, EditFieldTheme.SINGLE_LINE);
      },
      codeFieldInfo(): FieldInfo {
        const ressource = this.identifiantIsNumber ? RessourceFwkControl(FwkControls.CODE_SMS) : RessourceFwkControl(FwkControls.CODE_MAIL);
        const type = EditFieldType.STRING;
        const precision = 2;
        const format = null;
        const mask = null;
        const focus = null;
        const live = null;
        return new FieldInfo(ressource, type, precision, mask, format, focus, live, EditFieldTheme.SINGLE_LINE);
      },
      rememberMeFieldInfo(): FieldInfo {
        const ressource = RessourceFwkControl(FwkControls.REMEMBER_ME);
        const type = EditFieldType.STRING;
        const precision = 2;
        const format = null;
        const mask = null;
        const focus = null;
        const live = null;
        return new FieldInfo(ressource, type, precision, mask, format, focus, live, EditFieldTheme.BASIC);
      }
    },
    created(): void {
      if (this.identifiantQuery != null) {
        this.setIdentifiant(this.identifiantQuery);
      }
    },

    methods: {
      checkCgu(): void {
        // @ts-expect-error : /
        if (!this.identifiantError && !this.$refs.IDENTIFIANT.touch()) {
          if (!this.isLoading) {
            this.isLoading = true;
            this.param.identifiant = this.identifiant;
            this.param.password = this.password;
            this.param.codeConnection = this.code.replace(' ', '');
            this.param.rememberMe = this.rememberMe;
            this.param.isMobile = this.isMobile;
            this.login();
          }
        }
      },
      login(): void {
        storeSession.login(this.param).then((answer: LoginAnswer) => {
          if (answer.accountDisabled) {
            this.showDisabledAccount();
            return;
          }
          if (answer.isDomtom) {
            this.showDomtomAccount(answer.html);
          } else if (answer.user != null) {
            this.CheckNotificationRight(answer.user);
            if (this.route.name != 'Dashboard') {
              this.router.push({ name: 'Dashboard' });
            }
          } else {
            if (answer.mode == ConnectionParamModes.CODE || answer.mode == ConnectionParamModes.NONE) {
              if (this.useCode) {
                this.error = true;
              }
              this.html = answer.html;
              this.useCode = true;
              this.setRememberMe(true);
            } else if (answer.mode == ConnectionParamModes.PASSWORD) {
              if (this.usePassword) {
                this.error = true;
              }
              this.usePassword = true;
              this.setRememberMe(true);
            } else if (answer.mode == ConnectionParamModes.CI98) {
              this.$emit('ci98', answer.token);
            } else if (answer.mode == ConnectionParamModes.DIALOG) {
              const dialog = new Dialog()
              dialog.label = RessourceAppCustom(AppCustoms.MIGRATION_IMPOSSIBLE).label.replace('$DIALOG', answer.html);
              Alerts.message(dialog)
            } else {
              // @ts-expect-error : generated method
              this.$refs.alert.openModal(this.loginErrorDialog).then((res) => {
                if (res == RessourceFwkButton(FwkButtons.REGISTER).label) {
                  this.$emit('showRegister')
                }
              });
            }
          }
          this.isLoading = false;
        }).catch(() => {
          this.isLoading = false;
        })
      },
      showDomtomAccount(html: string): void {
        this.clear();
        this.error = false;
        this.html = '';
        this.password = '';
        this.code = '';
        this.param.password = '';
        this.param.codeConnection = '';
        const dialog = new Dialog();
        dialog.label = html;
        Alerts.message(dialog);
      },
      showDisabledAccount(): void {
        this.clear();
        this.error = false;
        this.html = '';
        this.password = '';
        this.code = '';
        this.param.password = '';
        this.param.codeConnection = '';
        const dialog = new Dialog();
        dialog.label = RessourceAppCustom(AppCustoms.LOGIN_ACCOUNT_DISABLED).label;
        Alerts.message(dialog);
      },
      setDelayReset() {
        this.delayReset = true;
        if (this.delay == 0) {
          this.delay = 2000;
        } else if (this.delay == 2000) {
          this.delay = 15000;
        } else {
          this.delay = this.delay * 2;
        }
        this.delayRemaining = this.delay;
        this.setDelayLabel();
        setTimeout(() => this.clearDelayReset(), 1000);
      },
      clearDelayReset() {
        this.delayRemaining -= 1000;
        if (this.delayRemaining > 0) {
          this.setDelayLabel();
          setTimeout(() => this.clearDelayReset(), 1000);
        } else {
          this.delayReset = false;
        }
      },
      setDelayLabel() {
        this.delayLabel = ' (' + (this.delayRemaining / 1000).toFixed(0) + ' s)';
      },
      resetIdent(): void {
        const param: LoginParam = new LoginParam();
        param.identifiant = this.identifiant;
        storeSession.resetParam(param).then((answer: LoginAnswer) => {
          if (answer.accountDisabled) {
            this.showDisabledAccount();
            return;
          }
          if (answer.isDomtom) {
            this.showDomtomAccount(answer.html);
            return;
          }
          this.html = answer.html;
          this.setDelayReset();
          if (answer.token != '' && answer.token != null) {
            storeSession.setToken(answer.token, answer.user.client98);
            this.$emit('ci98');
          }
        });
      },
      CheckNotificationRight(user: User): void {
        if ('Notification' in window && Notification.permission !== 'granted') {
          Notification.requestPermission().then((permission) => {
            if (permission !== 'granted') {
              if (UserProfile.hasProfile(user, EnumProfile.support)) {
                const dialog = new Dialog()
                dialog.label = 'Cette application ne peut être utilisée<br/><b>sans notifications</b>.<br/>Merci de modifier cette autorisation dans les préférences de votre navigateur';
                Alerts.message(dialog)
              }
            }
          });
        }
      },
      clear(): void {
        this.useCode = false;
        this.usePassword = false;
      },
      setIdentifiant(value: string) {
        this.error = false;
        this.identifiant = value;
        this.password = '';
        this.$emit('updateIdentifiant', value);
      },
      setIdentifiantError(value: FieldError) {
        this.identifiantError = value.error;
      },
      setPassword(value: string) {
        this.error = false;
        this.password = value;
      },
      setPasswordError(value: FieldError) {
        this.passwordError = value.error;
      },
      setCode(value: string) {
        this.error = false;
        this.code = value;
      },
      setCodeError(value: FieldError) {
        this.codeError = value.error;
      },
      setRememberMe(value: boolean) {
        this.rememberMe = value;
      },
      setRememberMeError(value: FieldError) {
        this.rememberMeError = value.error;
      }
    }
  })

</script>

<style>
  .titre1 {
    font-size: 30px;
    font-weight: 500;
    color: #010606;
  }

  .reset-link {
    text-decoration: none;
    font-weight: bold;
    font-size: 14px;
    color: #e42724 !important;
    cursor: pointer;
    transition: color 0.3s, text-decoration 0.3s;
    display: inline-block;
    margin-bottom: 10px;
  }

    .reset-link:hover {
      text-decoration: underline;
      color: #c01f1b;
      cursor: pointer;
    }
</style>
